import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';

/**
 * As tarefas do agendador: o que cada uma concluiu, e a saúde da CADEIA.
 *
 * ## Por que este contrato existe
 *
 * Auditoria de 2026-09-06, achados F15 e F16. O agendador subia, montava a
 * cadeia de cada tarefa e esperava o PRÓXIMO horário: um reinício às 06:30
 * deixava o dia editorial sem geração até as 06:00 do dia seguinte. Para ele
 * recuperar sozinho a rodada vencida precisa PERGUNTAR o que já concluiu, e
 * essa memória não existia em lugar nenhum.
 *
 * ## As duas saúdes
 *
 * `workers/sinal-de-vida.ts` responde **o processo está andando?**: as cadeias
 * reagendaram dentro do prazo que declararam. Ele mora na tmpfs e morre com o
 * processo, de propósito, porque é isso que uma sonda de container precisa
 * saber.
 *
 * Este contrato responde a OUTRA pergunta, **a cadeia está funcionando?**:
 * quando foi a última execução, o próximo disparo, o atraso e o motivo da
 * falha, por tarefa. Um agendador que reagenda religiosamente uma tarefa que
 * falha há três dias passa na primeira pergunta e reprova na segunda. A
 * auditoria pediu a separação justamente porque juntar as duas produz um verde
 * que não significa nada.
 */

/**
 * O nome da tarefa.
 *
 * Fechado no contrato de propósito: é ele que a coluna `task` guarda e o painel
 * lê. Tarefa nova entra aqui, e o compilador cobra os dois lados.
 */
export const SchedulerTaskNameSchema = z.enum([
  'publicacao',
  'reconciliacao',
  'mapa',
  'metricas',
  'autonomia',
  'editorial',
  'noticias',
  'radar',
  'token',
  'limpeza',
]);
export type SchedulerTaskName = z.infer<typeof SchedulerTaskNameSchema>;

/**
 * O desfecho de um disparo, no vocabulário do agendador.
 *
 * `nada_a_fazer` é o 409, e ele NÃO é falha: o dia editorial já pronto responde
 * `RUN_ALREADY_READY`, e foi exatamente isso que a rodada das 06:00 respondeu em
 * produção em 2026-09-06. `nao_ligado` é o 501, o estado normal enquanto o dono
 * não conecta a conta da Meta. Só `falha` merece alarme.
 */
export const SchedulerTaskOutcomeSchema = z.enum([
  'ok',
  'nada_a_fazer',
  'nao_ligado',
  'falha',
]);
export type SchedulerTaskOutcome = z.infer<typeof SchedulerTaskOutcomeSchema>;

/** Data civil no fuso da operação (America/Sao_Paulo). */
export const OperationLocalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u);

/** Horário alvo de um disparo diário, em `HH:MM` local. */
export const SchedulerSlotSchema = z.string().regex(/^[0-2]\d:[0-5]\d$/u);

/**
 * O código do motivo da falha.
 *
 * MAIÚSCULAS e sublinhado: é o `code` do envelope de erro da própria API, ou um
 * código do transporte (`REDE_INDISPONIVEL`, `TEMPO_LIMITE`). Texto remoto e
 * mensagem de exceção ficam de fora, e a regra é a mesma do resto da operação:
 * o que entra no painel tem vocabulário nosso.
 */
export const SchedulerFailureCodeSchema = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{2,79}$/u);

export const ReportSchedulerTaskRequestSchema = z.strictObject({
  /** `instagram_accounts.id` do ambiente deste processo. Conferência, não escolha. */
  accountId: UuidV4Schema,
  task: SchedulerTaskNameSchema,
  outcome: SchedulerTaskOutcomeSchema,
  /**
   * O motivo, quando houve um. Ausente é diferente de nulo: um desfecho bom não
   * manda o campo, e um desfecho ruim sem código nomeado manda `null`.
   */
  failureCode: SchedulerFailureCodeSchema.nullable().optional(),
  /**
   * O disparo diário que este desfecho representa. Só tarefa com horário
   * declarado manda os dois; tarefa de cadência não tem disparo nomeado.
   */
  localDate: OperationLocalDateSchema.nullable().optional(),
  slot: SchedulerSlotSchema.nullable().optional(),
  /** Quando o agendador pretende disparar de novo. */
  nextRunAt: IsoUtcDateTimeSchema.nullable().optional(),
  /**
   * Este disparo foi uma RECUPERAÇÃO de rodada vencida, e não o tique normal.
   *
   * Ausente vale `false`. O servidor marca a tentativa qualquer que tenha sido
   * o desfecho, e é isso que faz a recuperação acontecer no máximo uma vez por
   * disparo: sem a marca, dez reinícios em cima de um dia que falhou tentariam
   * o mesmo dia dez vezes.
   */
  recovery: z.boolean().optional(),
});
export type ReportSchedulerTaskRequest = z.infer<
  typeof ReportSchedulerTaskRequestSchema
>;

export const SchedulerTaskStateSchema = z.strictObject({
  task: SchedulerTaskNameSchema,
  /** Quando o último disparo terminou, qualquer que tenha sido o desfecho. */
  lastRunAt: IsoUtcDateTimeSchema,
  outcome: SchedulerTaskOutcomeSchema,
  failureCode: SchedulerFailureCodeSchema.nullable(),
  /** Quando o último desfecho BOM aconteceu. Nulo é "nunca deu certo". */
  lastSuccessAt: IsoUtcDateTimeSchema.nullable(),
  /** O disparo diário que concluiu por último. Nulo em tarefa de cadência. */
  lastSuccessLocalDate: OperationLocalDateSchema.nullable(),
  lastSuccessSlot: SchedulerSlotSchema.nullable(),
  /**
   * O disparo diário que a recuperação já tentou, com desfecho qualquer.
   *
   * É a cerca contra reinício em série. O agendador a consulta no arranque
   * junto com o último sucesso: um disparo que já teve sucesso OU que já teve
   * uma recuperação não é recuperado de novo.
   */
  lastRecoveryLocalDate: OperationLocalDateSchema.nullable(),
  lastRecoverySlot: SchedulerSlotSchema.nullable(),
  nextRunAt: IsoUtcDateTimeSchema.nullable(),
  /**
   * Quanto o próximo disparo já passou da hora, em milissegundos.
   *
   * Zero quando está no prazo. Calculado no servidor a partir de `nextRunAt`
   * porque quem lê não pode precisar de uma segunda cópia da cadência: é a
   * mesma decisão do `prazoMs` do sinal de vida.
   */
  atrasoMs: z.int().nonnegative(),
});
export type SchedulerTaskStateDto = z.infer<typeof SchedulerTaskStateSchema>;

export const SchedulerTasksResponseSchema = z.strictObject({
  tasks: z.array(SchedulerTaskStateSchema),
});
export type SchedulerTasksResponse = z.infer<
  typeof SchedulerTasksResponseSchema
>;

// ---------------------------------------------------------------------------
// A limpeza das cópias de entrega
// ---------------------------------------------------------------------------

export const MediaSweepRequestSchema = z.strictObject({
  accountId: UuidV4Schema,
  /**
   * Teto de concessões por rodada.
   *
   * Existe porque a varredura é destrutiva e roda sozinha: um lote sem teto
   * numa base velha faria uma rodada segurar o disco por minutos. Ausente, vale
   * o `SWEEP_LIMIT` do serviço.
   */
  limit: z.int().positive().max(1_000).optional(),
});
export type MediaSweepRequest = z.infer<typeof MediaSweepRequestSchema>;

export const MediaSweepResponseSchema = z.strictObject({
  /** Concessões que passaram a não entregar mais nesta rodada. */
  revoked: z.int().nonnegative(),
  /** Cópias que saíram do disco nesta rodada. */
  purged: z.int().nonnegative(),
  /** Exclusões que falharam e voltam na rodada seguinte. */
  failed: z.int().nonnegative(),
  /** Quantas continuam esperando exclusão física. */
  pending: z.int().nonnegative(),
});
export type MediaSweepResponse = z.infer<typeof MediaSweepResponseSchema>;

// ---------------------------------------------------------------------------
// As falhas da operação, para quem desenhar a tela
// ---------------------------------------------------------------------------

/**
 * As cinco falhas que a auditoria mandou dar destino operacional.
 *
 * Elas viram ALERTA na tabela `alerts`, que já tem a deduplicação que o pedido
 * exige: índice único de um alerta aberto por código e recurso. Sem ela, um
 * verificador de minuto em minuto abriria 1.440 cópias do mesmo problema por
 * dia, e painel cheio de repetição é painel que ninguém lê.
 *
 * NOTIFICAÇÃO é outra coisa, e ela NÃO existe nesta pilha: não há canal
 * autorizado (nem e-mail, nem mensagem, nem webhook de alerta) configurado em
 * lugar nenhum do código em 2026-09-06. O pedido diz "notificações usam canal
 * previamente autorizado", e criar um canal novo seria inventar a autorização.
 * O que existe é a coluna `alerts.last_notified_at`, esperando o dia em que o
 * dono autorizar um canal.
 */
export const OperationFailureCodeSchema = z.enum([
  /** O dia editorial terminou sem a peça: `partial`, `failed` ou alerta de peça. */
  'EDITORIAL_PIECE_MISSING',
  /** A execução do dia não chegou a `ready`. */
  'EDITORIAL_RUN_PARTIAL',
  /** O horário de publicação passou sem a peça sair. */
  'PUBLISHING_SLOT_EXPIRED',
  /** A Meta recusou o token: 401, 190, ou saúde reprovada. */
  'META_TOKEN_REFUSED',
  /** A coleta de métricas ou de notícias está atrasada além da cadência. */
  'COLLECTION_DELAYED',
]);
export type OperationFailureCode = z.infer<typeof OperationFailureCodeSchema>;
